import React, { useEffect, useState, useRef, useCallback } from 'react';
import './style.scss';
import { IMAGES, TWO_THOUSAND_CONST } from 'src/shared/utils/appConstant';
import { CONTACT_API_URL, isValidEmail, isValidPhone, isValidName, MAX_MESSAGE_LENGTH, MAX_NAME_LENGTH, MAX_EMAIL_LENGTH, MAX_PHONE_LENGTH, NAME_PATTERN, PHONE_PATTERN } from 'src/shared/utils/string';
import SuccessFullModal from 'src/components/contact/successfullModal';
import ReCAPTCHA from 'react-google-recaptcha';

interface IContactForm {
  name: string;
  email: string;
  phone: string;
  message: string;
  "formId": "wmp-contact-form-v1",
}

const ContactForm: React.FC = () => {
  const [contact, setContact] = useState<IContactForm>({
    name: '',
    email: '',
    phone: '',
    message: '',
    "formId": "wmp-contact-form-v1",
  });

  const [showModal, setShowModal] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA | null>(null);

  useEffect(() => {
    const interval = setTimeout(() => {
      // Increment the activeIndex to move to the next slide
      setShowModal(false);
      setContact({
        name: '',
        email: '',
        phone: '',
        message: '',
        "formId": "wmp-contact-form-v1",
      });
      // Reset recaptcha
      recaptchaRef.current?.reset();
      setRecaptchaToken(null);
    }, TWO_THOUSAND_CONST); // Close the modal after 2 seconds

    // Clear the timeout when the component is unmounted
    return () => clearTimeout(interval);
  }, [showModal]);


  const [errors, setErrors] = useState<Record<string, boolean>>({
    name: false,
    email: false,
    phone: false,
    message: false,
    recaptcha: false,
  });

  const handleChangeName = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (value.length === 0) {
      setErrors((prevErrors) => ({ ...prevErrors, [name]: false }));
    }
    setContact(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleChangeEmail = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (value.length === 0) {
      setErrors((prevErrors) => ({ ...prevErrors, [name]: false}));
    }
    setContact(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleChangePhone = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (value.length === 0) {
      setErrors((prevErrors) => ({ ...prevErrors, [name]: false}));
    }
    setContact(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleRecaptchaChange = useCallback((token: string | null) => {
    setRecaptchaToken(token);
    if (token) {
      setErrors((prevErrors) => ({ ...prevErrors, recaptcha: false }));
    }
  }, []);

  const handleChangeMessage = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setContact(prev => ({ ...prev, [name]: value }));
  }, []);
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitError(null);
    validateForm();

    if (isFormValid() && recaptchaToken) {
      setSubmitting(true);
      try {
        const response = await fetch(CONTACT_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            "Access-Control-Allow-Headers": "*",
            "Access-Control-Allow-Origin": "*",
          },
          body: JSON.stringify({ ...contact, recaptchaToken }),
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(errorText || 'Failed to submit form');
        }
        
        setShowModal(true);
        recaptchaRef.current?.reset();
        setRecaptchaToken(null);
      } catch (error) {
        console.error('Form submission error:', error);
        setSubmitError('Failed to submit form. Please try again.');
      } finally {
        setSubmitting(false);
      }
    }
  };

  const validateForm = () => {
    const { name, email, message, phone } = contact;
    const messageValid = message.trim().length <= MAX_MESSAGE_LENGTH;

    setErrors({
      name: !isValidName(name),
      email: !isValidEmail(email),
      phone: !isValidPhone(phone),
      message: !messageValid,
      recaptcha: !recaptchaToken,
    });
  };

  const validateName = () => {
    setErrors((prevErrors) => {
      const { name } = contact;
      return {
        ...prevErrors,
        name: name.length > 0 ? !isValidName(name) : false,
      };
    });
  }

  const validateEmail = () => {
    setErrors((prevErrors) => {
      const { email } = contact;
      return {
        ...prevErrors,
        email: email.length > 0 ? !isValidEmail(email) : false,
      };
    });
  }

  const validatePhone = () => {
    setErrors((prevErrors) => {
      const { phone } = contact;
      return {
        ...prevErrors,
        phone: phone.length > 0 ? !isValidPhone(phone) : false,
      };
    });
  }

  const isFormValid = () => {
    return Object.values(errors).every((error) => !error);
  };

  return (
    <><div>
      <form className="contact-form" onSubmit={handleSubmit}>
        <div className='input-container'>
          <img className="input-icon" src={IMAGES.USER_ICON} alt="icon" />
          <input
            type="text"
            name="name"
            value={contact.name}
            onChange={handleChangeName}
            placeholder='Full Name*'
            className={`contact-input`}
            onBlur={validateName}
            maxLength={MAX_NAME_LENGTH}
            pattern={NAME_PATTERN}
            required />
          {/* {errors.name && <div className='notched-div bottom'>Invalid name</div>} */}
          {errors.name && <p className="error-popup">Invalid Name</p>}
        </div>
        <div className='input-container'>
          <img className="input-icon mail-img" src={IMAGES.MAIL_CONTACT_ICON} alt="icon" />
          <input
            type="email"
            name="email"
            value={contact.email}
            onChange={handleChangeEmail}
            placeholder='Email Address*'
            className={`contact-input`}
            onBlur={validateEmail}
            maxLength={MAX_EMAIL_LENGTH}
            required />
          {/* {errors.email && <div className='notched-div bottom'>Invalid Email</div>} */}
          {errors.email && <p className="error-popup">Invalid Email</p>}
        </div>
        <div className='input-container'>
          <img className="input-icon phone-img" src={IMAGES.PHONE_CONTACT_ICON} alt="icon" />
          <input
            type="tel"
            name="phone"
            value={contact.phone}
            placeholder='Contact Number*'
            onChange={handleChangePhone}
            className={`contact-input`}
            onBlur={validatePhone}
            maxLength={MAX_PHONE_LENGTH}
            pattern={PHONE_PATTERN}
            required />
          {/* {errors.email && <div className='notched-div bottom'>Invalid Number</div>} */}
          {errors.phone && <p className="error-popup">Invalid Number</p>}
        </div>
        <div className='input-container'>
          <textarea
            name="message"
            value={contact.message}
            onChange={handleChangeMessage}
            placeholder='Message'
            className={`contact-textarea`}
            maxLength={MAX_MESSAGE_LENGTH}
          />
        </div>
        <div className='recaptcha-container'>
          {process.env.REACT_APP_RECAPTCHA_SITE_KEY && (
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
              onChange={handleRecaptchaChange}
            />
          )}
          {errors.recaptcha && <p className="error-popup" role="alert">Please complete the reCAPTCHA</p>}
        </div>
        {submitError && <p className="error-popup" role="alert">{submitError}</p>}
        <button className='contact-btn' type='submit' disabled={submitting}>
          {submitting ? 'Submitting...' : 'Submit'}
        </button>
      </form>
    </div>
      {showModal && <SuccessFullModal />}
    </>

  );
};

export default ContactForm;